import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import {
  AnalyticalChart,
  axisLabel,
  chartText,
  chartTooltip,
  palette,
  valueGrid,
} from "./charting";
import {
  demandDrivers,
  expectedHigh,
  expectedLow,
  interventions,
  materialChanges,
  periods,
  rootCauseHypotheses,
  themeHierarchy,
  weeklyVolume,
} from "./fixtures";

const categoryAxis = (data: string[]) => ({
  type: "category" as const,
  data,
  axisLine: { lineStyle: { color: palette.line } },
  axisTick: { show: false },
  axisLabel: { color: palette.muted, fontSize: 9 },
});
const valueAxis = (suffix = "") => ({
  type: "value" as const,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: axisLabel((v) => `${v}${suffix}`),
  splitLine: { lineStyle: { color: palette.line, type: "dashed" as const } },
});

export function ExplainedWaterfall({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (name: string) => void;
}) {
  const change = materialChanges[0]!;
  const names = [
    "Baseline",
    ...change.contributors.map((c) => c.label),
    ...change.offsets.map((c) => c.label),
    "Residual",
    "Current",
  ];
  const values = [
    change.baseline,
    ...change.contributors.map((c) => c.value),
    ...change.offsets.map((c) => c.value),
    change.residual,
    change.currentValue,
  ];
  let runningTotal = change.baseline;
  const positions = values.map((value, index) => {
    if (index === 0 || index === values.length - 1) return 0;
    const position = value < 0 ? runningTotal + value : runningTotal;
    runningTotal += value;
    return position;
  });
  const option = useMemo<EChartsOption>(
    () => ({
      textStyle: chartText,
      tooltip: {
        ...chartTooltip,
        trigger: "axis",
        formatter: "{b}<br/><strong>{c1} interactions</strong>",
      },
      grid: valueGrid(),
      xAxis: categoryAxis(names),
      yAxis: {
        ...valueAxis(),
        min: 48000,
        max: 58000,
        axisLabel: axisLabel((v) => `${Math.round(v / 1000)}k`),
      },
      series: [
        {
          name: "Position",
          type: "bar",
          stack: "total",
          silent: true,
          itemStyle: { color: "transparent" },
          data: positions,
        },
        {
          name: "Change",
          type: "bar",
          stack: "total",
          barWidth: 24,
          data: values.map((value, index) => ({
            value:
              index === 0 || index === values.length - 1
                ? value
                : Math.abs(value),
            itemStyle: {
              color:
                index === 0 || index === values.length - 1
                  ? palette.ink
                  : value < 0
                    ? palette.green
                    : selected === names[index]
                      ? palette.red
                      : palette.amber,
              borderColor:
                selected === names[index] ? palette.ink : "transparent",
              borderWidth: selected === names[index] ? 2 : 0,
            },
          })),
          label: {
            show: true,
            position: "top",
            fontSize: 9,
            color: palette.ink,
            formatter: (p) => {
              const index = typeof p.dataIndex === "number" ? p.dataIndex : 0;
              return (values[index] ?? 0).toLocaleString();
            },
          },
        },
      ],
    }),
    [change, names, positions, selected, values],
  );
  return (
    <AnalyticalChart
      option={option}
      label="Explained contact-volume waterfall"
      question="Which changes explain the 6,242 additional interactions?"
      detail={names.map((label, index) => ({
        label,
        values: [values[index] ?? 0],
      }))}
      className="chart-large"
      onSelect={onSelect}
    />
  );
}

export function AnnotatedTrend({ signal = false }: { signal?: boolean }) {
  const option = useMemo<EChartsOption>(
    () => ({
      textStyle: chartText,
      tooltip: { ...chartTooltip, trigger: "axis" },
      grid: valueGrid(),
      legend: {
        top: 0,
        right: 0,
        textStyle: { fontSize: 9, color: palette.muted },
      },
      xAxis: categoryAxis(periods),
      yAxis: {
        ...valueAxis(),
        min: 48000,
        axisLabel: axisLabel((v) => `${Math.round(v / 1000)}k`),
      },
      series: [
        {
          name: "Expected low",
          type: "line",
          data: expectedLow,
          stack: "range",
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { opacity: 0 },
        },
        {
          name: "Expected range",
          type: "line",
          data: expectedHigh.map((v, i) => v - expectedLow[i]!),
          stack: "range",
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "rgba(50,104,135,.14)" },
        },
        {
          name: "Observed",
          type: "line",
          data: weeklyVolume,
          smooth: 0.2,
          symbolSize: 5,
          lineStyle: { color: palette.green, width: 2.5 },
          itemStyle: { color: palette.green },
          markLine: {
            symbol: "none",
            lineStyle: { color: palette.red, type: "dashed" },
            label: {
              formatter: "Android 8.14",
              fontSize: 9,
              color: palette.red,
            },
            data: [{ xAxis: "1 Jul" }],
          },
        },
      ],
    }),
    [],
  );
  return (
    <AnalyticalChart
      option={option}
      label="Observed contact volume against expected range"
      question={
        signal
          ? "When did the observation move beyond expected variation?"
          : "Did the period move beyond the expected range, and when?"
      }
      detail={periods.map((label, index) => ({
        label,
        values: [
          expectedLow[index]!,
          weeklyVolume[index]!,
          expectedHigh[index]!,
        ],
      }))}
      className="chart-medium"
    />
  );
}

export function PriorityMatrix() {
  const shortNames = [
    "Assisted contact",
    "Identity abandonment",
    "Delivery reassurance",
    "Renewal handling",
  ];
  const rows = materialChanges.map((m, index) => ({
    name: shortNames[index] ?? m.title,
    fullName: m.title,
    value: [
      Math.abs(m.relativeChange),
      index === 0 ? 38 : index === 1 ? 49 : index === 2 ? 13 : 17,
      m.evidenceCount,
      m.confidence.value,
    ],
    itemStyle: {
      color: m.positive
        ? palette.green
        : index < 2
          ? palette.red
          : palette.amber,
    },
  }));
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: {
      ...chartTooltip,
      formatter:
        "{b}<br/>Impact: {@[0]}%<br/>Acceleration: {@[1]}%<br/>{@[2]} evidence records<br/>{@[3]}% confidence",
    },
    grid: valueGrid(),
    xAxis: {
      ...valueAxis("%"),
      name: "Operational impact",
      nameLocation: "middle",
      nameGap: 28,
    },
    yAxis: {
      ...valueAxis("%"),
      name: "Acceleration",
      nameLocation: "middle",
      nameGap: 34,
    },
    series: [
      {
        type: "scatter",
        data: rows,
        symbolSize: (value: number[]) =>
          Math.max(28, Math.sqrt(value[2] ?? 1) * 1.25),
        label: {
          show: true,
          position: "top",
          fontSize: 9,
          color: palette.ink,
          formatter: "{b}",
        },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Priority matrix of material changes"
      question="Which changes combine impact, acceleration, volume and credible evidence?"
      detail={rows.map((r) => ({ label: r.fullName, values: r.value }))}
      className="chart-medium"
    />
  );
}

const heatmapCohorts = [
  "Android 14",
  "Plan changers",
  "Insurance renewal",
  "Digital only",
];
const heatmapMetrics = ["Volume", "Repeat", "Effort", "Unresolved"];
const heatmapData = [
  [0, 0, 42],
  [1, 0, 18],
  [2, 0, 11],
  [3, 0, -8],
  [0, 1, 31],
  [1, 1, 38],
  [2, 1, 22],
  [3, 1, -12],
  [0, 2, 44],
  [1, 2, 21],
  [2, 2, 28],
  [3, 2, -16],
  [0, 3, 36],
  [1, 3, 17],
  [2, 3, 19],
  [3, 3, -9],
];
export function CohortHeatmap({
  title = "Where is change concentrated?",
}: {
  title?: string;
}) {
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip, formatter: "{b}: {c}% deviation" },
    grid: { left: 105, right: 20, top: 20, bottom: 36 },
    xAxis: categoryAxis(heatmapMetrics),
    yAxis: categoryAxis(heatmapCohorts),
    visualMap: {
      show: false,
      min: -20,
      max: 45,
      inRange: { color: ["#dbeae5", palette.paper, "#d49b62", palette.red] },
    },
    series: [
      {
        type: "heatmap",
        data: heatmapData,
        label: { show: true, fontSize: 9, formatter: "{@[2]}%" },
        itemStyle: { borderColor: palette.paper, borderWidth: 3 },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Cohort deviation heatmap"
      question={title}
      detail={heatmapCohorts.map((label, y) => ({
        label,
        values: heatmapData.filter((d) => d[1] === y).map((d) => `${d[2]}%`),
      }))}
      className="chart-medium"
    />
  );
}

export function IncidentTimeline() {
  const events = [
    { name: "Android 8.14 release", value: ["2 Jul", 3] },
    { name: "Campaign launch", value: ["6 Jul", 1] },
    { name: "Billing rules change", value: ["8 Jul", 2] },
    { name: "Tracking copy launch", value: ["8 Jul", 0] },
  ];
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip },
    grid: { left: 95, right: 20, top: 20, bottom: 35 },
    xAxis: categoryAxis([
      "1 Jul",
      "2 Jul",
      "4 Jul",
      "6 Jul",
      "8 Jul",
      "10 Jul",
      "12 Jul",
      "14 Jul",
    ]),
    yAxis: categoryAxis(["Product", "Process", "Campaign", "Release"]),
    series: [
      {
        type: "scatter",
        symbolSize: 16,
        data: events,
        itemStyle: { color: palette.green },
        label: { show: true, position: "right", fontSize: 9, formatter: "{b}" },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Operational incident timeline"
      question="Which operational events align in time without proving cause?"
      detail={events.map((e) => ({ label: e.name, values: e.value }))}
      className="chart-compact"
    />
  );
}

export function ThemeHierarchyChart({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (name: string) => void;
}) {
  return (
    <div
      className="theme-flow"
      aria-label="Interactive customer theme hierarchy"
    >
      <div className="flow-heading">
        <span>Theme</span>
        <span>Subtheme</span>
        <span>Specific need</span>
        <span>Volume</span>
      </div>
      {themeHierarchy.map((theme) => (
        <button
          type="button"
          className={selected === theme.name ? "selected" : ""}
          onClick={() => onSelect(theme.name)}
          key={theme.id}
        >
          <strong>{theme.name}</strong>
          <span>{theme.children[0].name}</span>
          <span>{theme.children[0].children[0]}</span>
          <span>
            <strong>{theme.volume.toLocaleString()}</strong>
            <small>
              {theme.change > 0 ? "+" : ""}
              {theme.change}%
            </small>
          </span>
        </button>
      ))}
      <p>
        Select a row to update the interpretation and local taxonomy actions.
      </p>
    </div>
  );
}

export function ThemeTrends() {
  const series = themeHierarchy.map((t, i) => ({
    name: t.name,
    type: "line" as const,
    symbol: "none",
    smooth: 0.2,
    data: Array.from({ length: 8 }, (_, j) =>
      Math.round(
        (t.volume * (0.72 + j * 0.04 + (i === 1 && j > 5 ? 0.08 : 0))) / 8,
      ),
    ),
  }));
  const option: EChartsOption = {
    textStyle: chartText,
    color: [palette.green, palette.red, palette.blue, palette.amber],
    tooltip: { ...chartTooltip, trigger: "axis" },
    legend: { top: 0, textStyle: { fontSize: 9 } },
    grid: valueGrid(),
    xAxis: categoryAxis(periods),
    yAxis: {
      ...valueAxis(),
      axisLabel: axisLabel((v) => `${(v / 1000).toFixed(1)}k`),
    },
    series,
  };
  return (
    <AnalyticalChart
      option={option}
      label="Ranked theme trends"
      question="Which customer needs are emerging, persistent or declining?"
      detail={themeHierarchy.map((t) => ({
        label: t.name,
        values: [t.volume, `${t.change}%`, `${t.persistence}% persistent`],
      }))}
      className="chart-medium"
    />
  );
}

export function DemandPareto() {
  const sorted = [...demandDrivers].sort((a, b) => b.volume - a.volume);
  let cumulative = 0;
  const total = 56480;
  const cum = sorted.map(
    (d) => (
      (cumulative += d.volume),
      Math.round((cumulative / total) * 1000) / 10
    ),
  );
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip, trigger: "axis" },
    grid: { left: 145, right: 45, top: 20, bottom: 28 },
    xAxis: [valueAxis(), { ...valueAxis("%"), min: 0, max: 100, show: false }],
    yAxis: { ...categoryAxis(sorted.map((d) => d.name)), inverse: true },
    series: [
      {
        type: "bar",
        data: sorted.map((d) => d.volume),
        barWidth: 12,
        itemStyle: { color: palette.green },
      },
      {
        type: "line",
        xAxisIndex: 1,
        data: cum,
        symbolSize: 5,
        lineStyle: { color: palette.amber },
        itemStyle: { color: palette.amber },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Demand driver Pareto"
      question="Which contact reasons account for most demand?"
      detail={sorted.map((d, i) => ({
        label: d.name,
        values: [d.volume, `${d.share}%`, `${cum[i]}% cumulative`],
      }))}
      className="chart-large"
    />
  );
}

export function DemandScatter() {
  const labelledDrivers = new Set([
    "Payment date confusion",
    "Identity verification failure",
    "Order status",
  ]);
  const data = demandDrivers.map((d) => ({
    name: d.name,
    value: [d.volume, d.repeatRate, d.serviceCost],
    symbolSize: Math.sqrt(d.serviceCost) / 3,
    itemStyle: {
      color:
        d.avoidability === "High"
          ? palette.red
          : d.avoidability === "Medium"
            ? palette.amber
            : palette.green,
    },
    label: {
      show: labelledDrivers.has(d.name),
      position:
        d.name === "Payment date confusion"
          ? ("left" as const)
          : ("top" as const),
    },
  }));
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: {
      ...chartTooltip,
      formatter:
        "{b}<br/>{@[0]} contacts<br/>{@[1]}% repeat<br/>£{@[2]} estimated service cost",
    },
    grid: valueGrid(),
    xAxis: {
      ...valueAxis(),
      name: "Volume",
      nameLocation: "middle",
      nameGap: 28,
    },
    yAxis: {
      ...valueAxis("%"),
      name: "Repeat rate",
      nameLocation: "middle",
      nameGap: 30,
    },
    series: [
      {
        type: "scatter",
        data,
        label: { show: true, position: "top", fontSize: 8, formatter: "{b}" },
        labelLayout: { hideOverlap: true },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Volume and repeat rate demand scatter"
      question="Which high-volume drivers also produce repeat contact? Bubble size is estimated cost."
      detail={demandDrivers.map((d) => ({
        label: d.name,
        values: [
          d.volume,
          `${d.repeatRate}%`,
          d.avoidability,
          `£${d.serviceCost.toLocaleString()}`,
        ],
      }))}
      className="chart-large"
    />
  );
}

export function SurvivalCurve({
  label = "Payment date confusion",
}: {
  label?: string;
}) {
  const days = [0, 1, 2, 3, 5, 7, 14, 21, 30],
    selected = [100, 88, 79, 74, 70, 66, 62, 60, 58],
    baseline = [100, 94, 91, 89, 86, 84, 82, 81, 80];
  const option: EChartsOption = {
    textStyle: chartText,
    color: [palette.red, palette.green],
    tooltip: { ...chartTooltip, trigger: "axis" },
    legend: { top: 0, textStyle: { fontSize: 9 } },
    grid: valueGrid(),
    xAxis: categoryAxis(days.map(String)),
    yAxis: { ...valueAxis("%"), min: 50, max: 100 },
    series: [
      { name: label, type: "line", data: selected, symbolSize: 5 },
      {
        name: "All demand",
        type: "line",
        data: baseline,
        symbolSize: 5,
        lineStyle: { type: "dashed" },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Repeat contact survival curve"
      question="What proportion remain without repeat contact as time passes?"
      detail={days.map((d, i) => ({
        label: `Day ${d}`,
        values: [`${selected[i]}%`, `${baseline[i]}%`],
      }))}
      className="chart-medium"
    />
  );
}

export function RootCauseSankey({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (name: string) => void;
}) {
  return (
    <div
      className="cause-flow"
      aria-label="Symptom to likely cause and outcome flow"
    >
      <div className="cause-flow__labels">
        <span>Observed symptom</span>
        <span>Contributing factor</span>
        <span>Likely condition</span>
        <span>Associated outcome</span>
      </div>
      {rootCauseHypotheses.map((hypothesis) => (
        <button
          type="button"
          className={
            selected === hypothesis.cause || selected === hypothesis.symptom
              ? "selected"
              : ""
          }
          onClick={() => onSelect(hypothesis.cause)}
          key={hypothesis.id}
        >
          <span>{hypothesis.symptom}</span>
          <i>→</i>
          <span>{hypothesis.contributingFactors[0]}</span>
          <i>→</i>
          <strong>{hypothesis.cause}</strong>
          <i>→</i>
          <span>{hypothesis.outcome}</span>
          <small>
            {hypothesis.explanatoryContribution}% contribution ·{" "}
            {hypothesis.confidence.value}% confidence
          </small>
        </button>
      ))}
      <p>
        Observed association only. Selecting a row updates the evidence sample.
      </p>
    </div>
  );
}

export function CauseGraph({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (name: string) => void;
}) {
  return (
    <div
      className="evidence-link-list"
      aria-label="Evidence strength by likely cause"
    >
      <div className="evidence-link-list__header">
        <span>Likely explanation</span>
        <span>Evidence balance</span>
        <span>Contribution · confidence</span>
      </div>
      {rootCauseHypotheses.map((hypothesis) => (
        <button
          type="button"
          className={selected === hypothesis.cause ? "selected" : ""}
          onClick={() => onSelect(hypothesis.cause)}
          key={hypothesis.id}
        >
          <strong>{hypothesis.title}</strong>
          <span>
            {hypothesis.supportingEvidenceIds.length} supporting ·{" "}
            {hypothesis.contradictoryEvidenceIds.length} contradictory
          </span>
          <span>
            {hypothesis.explanatoryContribution}% ·{" "}
            {hypothesis.confidence.value}%
          </span>
        </button>
      ))}
      <p>
        Select a row to update the hypothesis narrative and evidence sample.
      </p>
    </div>
  );
}

export function ContributionBars() {
  const rows = [
    ...rootCauseHypotheses.map((h) => ({
      name: h.title,
      value: h.explanatoryContribution,
    })),
    { name: "Unexplained residual", value: 32 },
  ];
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip, formatter: "{b}: {c}%" },
    grid: { left: 190, right: 30, top: 20, bottom: 28 },
    xAxis: { ...valueAxis("%"), max: 40 },
    yAxis: { ...categoryAxis(rows.map((r) => r.name)), inverse: true },
    series: [
      {
        type: "bar",
        data: rows.map((r, i) => ({
          value: r.value,
          itemStyle: {
            color: i === rows.length - 1 ? palette.muted : palette.green,
          },
        })),
        barWidth: 16,
        label: {
          show: true,
          position: "right",
          formatter: "{c}%",
          fontSize: 9,
        },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Root cause contribution including residual"
      question="How much of the observed movement does each hypothesis explain?"
      detail={rows.map((r) => ({ label: r.name, values: [`${r.value}%`] }))}
      className="chart-compact"
    />
  );
}

export function JourneySankey() {
  return (
    <div
      className="journey-flow"
      aria-label="Cross-channel identity verification flow"
    >
      <div className="journey-flow__start">
        <span>100%</span>
        <strong>Start in app</strong>
        <small>Identity verification</small>
      </div>
      <i>→</i>
      <div className="journey-flow__failure">
        <span>42%</span>
        <strong>Document capture fails</strong>
        <small>Customer leaves self-service</small>
      </div>
      <i>→</i>
      <div className="journey-flow__branches">
        <div>
          <span>24%</span>
          <strong>Switch to chat</strong>
        </div>
        <div>
          <span>18%</span>
          <strong>Call support</strong>
        </div>
      </div>
      <i>→</i>
      <div className="journey-flow__outcome">
        <span>12%</span>
        <strong>Contact again</strong>
        <small>after manual review</small>
      </div>
      <p>
        58% resolve in self-service. The visible path follows the 42% who switch
        to assisted channels.
      </p>
    </div>
  );
}

export function EffortDistribution() {
  const bands = ["1–2", "3–4", "5–6", "7–8", "9–10"];
  const inferred = [8, 17, 26, 31, 18],
    reported = [11, 20, 29, 27, 13];
  const option: EChartsOption = {
    textStyle: chartText,
    color: [palette.green, palette.blue],
    tooltip: { ...chartTooltip, trigger: "axis" },
    legend: { top: 0, textStyle: { fontSize: 9 } },
    grid: valueGrid(),
    xAxis: categoryAxis(bands),
    yAxis: valueAxis("%"),
    series: [
      { name: "Inferred effort", type: "bar", data: inferred, barWidth: 16 },
      {
        name: "Customer-reported CES",
        type: "line",
        data: reported,
        symbolSize: 6,
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Inferred effort and customer-reported CES distributions"
      question="Do behavioural effort signals align with reported customer effort?"
      detail={bands.map((b, i) => ({
        label: b,
        values: [`${inferred[i]}% inferred`, `${reported[i]}% reported`],
      }))}
      className="chart-medium"
    />
  );
}

export function InterventionTrend({ index = 0 }: { index?: number }) {
  const item = interventions[index] ?? interventions[0]!;
  const labels = item.actualSeries.map((_, i) => `Week ${i + 1}`);
  const option: EChartsOption = {
    textStyle: chartText,
    color: [palette.green, palette.blue],
    tooltip: { ...chartTooltip, trigger: "axis" },
    legend: { top: 0, textStyle: { fontSize: 9 } },
    grid: valueGrid(),
    xAxis: categoryAxis(labels),
    yAxis: valueAxis(),
    series: [
      {
        name: "Target cohort",
        type: "line",
        data: item.actualSeries,
        symbolSize: 5,
        lineStyle: { width: 2.5 },
      },
      {
        name: "Comparison",
        type: "line",
        data: item.comparisonSeries,
        symbolSize: 4,
        lineStyle: { type: "dashed" },
        markArea: {
          silent: true,
          itemStyle: { color: "rgba(23,107,91,.08)" },
          data: [[{ xAxis: "Week 2" }, { xAxis: labels[labels.length - 1]! }]],
        },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Intervention before and after trend"
      question="Did the target cohort improve relative to comparison after launch?"
      detail={labels.map((l, i) => ({
        label: l,
        values: [item.actualSeries[i]!, item.comparisonSeries[i]!],
      }))}
      className="chart-large"
    />
  );
}

export function ActionPortfolio() {
  const shortLabels = [
    "Delivery tracking",
    "Android rollback",
    "Renewal test",
    "Billing alignment",
  ];
  const data = interventions.map((x, i) => ({
    name: shortLabels[i] ?? x.title,
    fullName: x.title,
    value: [Math.abs(x.baseline - x.target), [75, 88, 61, 83][i] ?? 0, i],
    symbolSize: 38,
    label: {
      position: (["left", "right", "bottom", "top"] as const)[i] ?? "top",
      distance: 7,
    },
    itemStyle: {
      color:
        x.status === "Effective"
          ? palette.green
          : x.status === "Inconclusive"
            ? palette.amber
            : palette.blue,
    },
  }));
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: {
      ...chartTooltip,
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const datum = item?.data as
          { fullName?: string; value?: (string | number)[] } | undefined;
        return `${datum?.fullName ?? item?.name ?? "Action"}<br/>Expected impact: ${datum?.value?.[0] ?? 0}<br/>Evidence strength: ${datum?.value?.[1] ?? 0}%`;
      },
    },
    grid: valueGrid(),
    xAxis: {
      ...valueAxis(),
      name: "Expected impact",
      nameLocation: "middle",
      nameGap: 28,
    },
    yAxis: {
      ...valueAxis("%"),
      name: "Evidence strength",
      nameLocation: "middle",
      nameGap: 30,
    },
    series: [
      {
        type: "scatter",
        data,
        label: {
          show: true,
          fontSize: 8,
          formatter: "{b}",
        },
        labelLayout: { hideOverlap: true },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Action portfolio impact and evidence matrix"
      question="Where should leadership commit attention based on expected impact and evidence strength?"
      detail={interventions.map((x, i) => ({
        label: x.title,
        values: [
          Math.round(Number(data[i]!.value[0] ?? 0)),
          `${data[i]!.value[1] ?? 0}%`,
          x.status,
        ],
      }))}
      className="chart-medium"
    />
  );
}

export function IntakeFunnel() {
  const rows = [
    { name: "Expected", value: 56480 },
    { name: "Received", value: 56180 },
    { name: "Eligible", value: 53932 },
    { name: "Processed", value: 53802 },
  ];
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip },
    grid: { left: 100, right: 30, top: 20, bottom: 25 },
    xAxis: { ...valueAxis(), min: 50000 },
    yAxis: { ...categoryAxis(rows.map((r) => r.name)), inverse: true },
    series: [
      {
        type: "bar",
        data: rows.map((r) => r.value),
        barWidth: 20,
        itemStyle: { color: palette.green },
        label: {
          show: true,
          position: "right",
          fontSize: 10,
          formatter: (p) => Number(p.value).toLocaleString(),
        },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Data intake eligibility and processing funnel"
      question="Where were records excluded or lost before analysis?"
      detail={rows.map((r) => ({ label: r.name, values: [r.value] }))}
      className="chart-medium"
    />
  );
}

export function CoverageCalendar() {
  const days = Array.from({ length: 28 }, (_, i) => [
    `2026-06-${String(21 + i).padStart(2, "0")}`,
    94 + (i % 6),
  ]);
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip, formatter: "{b}: {c}% coverage" },
    visualMap: {
      show: false,
      min: 90,
      max: 100,
      inRange: { color: ["#dfb47f", palette.green] },
    },
    calendar: {
      top: 22,
      left: 42,
      right: 20,
      cellSize: ["auto", 20],
      range: ["2026-06-21", "2026-07-18"],
      itemStyle: { borderColor: palette.paper, borderWidth: 3 },
      splitLine: { show: false },
      dayLabel: { fontSize: 9 },
      monthLabel: { fontSize: 9 },
      yearLabel: { show: false },
    },
    series: [{ type: "heatmap", coordinateSystem: "calendar", data: days }],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Analysis coverage calendar heatmap"
      question="Were any dates materially under-covered?"
      detail={days.map((d) => ({ label: String(d[0]), values: [`${d[1]}%`] }))}
      className="chart-compact"
    />
  );
}

export function LagTrend() {
  const lag = [5, 6, 6, 8, 7, 18, 14, 9];
  const option: EChartsOption = {
    textStyle: chartText,
    tooltip: { ...chartTooltip, trigger: "axis" },
    grid: valueGrid(),
    xAxis: categoryAxis(periods),
    yAxis: { ...valueAxis("m"), max: 25 },
    series: [
      {
        type: "line",
        data: lag,
        lineStyle: { color: palette.green, width: 2 },
        itemStyle: { color: palette.green },
        markLine: {
          symbol: "none",
          lineStyle: { color: palette.red, type: "dashed" },
          label: { formatter: "15m threshold", fontSize: 9 },
          data: [{ yAxis: 15 }],
        },
      },
    ],
  };
  return (
    <AnalyticalChart
      option={option}
      label="Connector lag against service threshold"
      question="Is source-to-analysis lag within the agreed threshold?"
      detail={periods.map((p, i) => ({
        label: p,
        values: [`${lag[i]} minutes`],
      }))}
      className="chart-compact"
    />
  );
}
